"use client";

/**
 * Auth context client-side. Adapta la sesión de Auth.js v5 (`useSession()`)
 * a la API que consumen los componentes — agrega el shift derivado y el
 * estado UI del idle-lock.
 *
 * - `session.user` viene de `useSession()` (NextAuth).
 * - `shift` y `shiftStartedAt` son **derivados client-side** (no persisten en
 *   DB). El shift se calcula por hora del día; el `shiftStartedAt` se cachea
 *   en sessionStorage al primer mount post-login.
 * - `locked` / `lock` / `unlock` son estado UI local (idle-lock) — no tocan
 *   server.
 * - `logout` llama `signOut` y redirige a /login.
 */
import { signOut, useSession } from "next-auth/react";
import { recordScreenLock } from "@/server/actions/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { showAuthTransition } from "@/components/auth/transition-overlay";
import { resetUserContext, setUserContext } from "@/lib/observability";
import { teardownNativePush } from "@/lib/push/native-push";
import { DEFAULT_TZ, setActiveTz } from "@/lib/tz";
import type { Role, TenantRole } from "@/db/schema";

export type Shift = "morning" | "afternoon" | "night";

export interface AuthSessionUser {
  id: string;
  name: string;
  email: string | null;
  documentId: string | null;
  // Contexto de agencia activo. Garantizado no-null acá: el memo trata
  // "autenticado sin contexto" como sesión null (solo pasa en el picker, fuera
  // del shell), así que los componentes de la app ven role/agency siempre.
  role: Role;
  tenantId: string;
  tenantSlug: string;
  // TZ del tenant (IANA, default Argentina). El AuthProvider la fija como TZ
  // activa del cliente (ver setActiveTz en @/lib/tz) para que todo el formateo
  // de fechas en la UI salga en la zona del tenant.
  tenantTimezone: string;
  agencyId: string;
  agencySlug: string;
  // Rol de tenant (superadmin) o null. multiContext = el user tiene más de una
  // agencia donde entrar → la UI muestra el switcher.
  tenantRole: TenantRole | null;
  multiContext: boolean;
  sessionId: string;
  mustResetPassword: boolean;
  active: boolean;
  // Modo consulta (piloto finalizado): solo lectura + cartel. Ver auth.config.
  pilotEnded: boolean;
}

export interface AuthSession {
  user: AuthSessionUser;
  shift: Shift;
  shiftStartedAt: string;
  locked?: boolean;
}

interface AuthContextValue {
  session: AuthSession | null;
  status: "loading" | "authenticated" | "unauthenticated";
  locked: boolean;
  lock: (reason?: "idle" | "manual") => void;
  unlock: () => void;
  logout: () => Promise<void>;
}

const SHIFT_STARTED_KEY = "vortigon.shift-started-at";
const LOCKED_KEY = "vortigon.locked";

// Guardamos el userId al que pertenece el lock. Así, si el usuario se logea
// con otra cuenta (o se cierra sesión y entra otro), el lock anterior se
// considera inválido y la app no muestra el LockScreen tras el login.
function readLockedUserId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LOCKED_KEY);
  } catch {
    return null;
  }
}

function writeLockedUserId(userId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (userId) window.localStorage.setItem(LOCKED_KEY, userId);
    else window.localStorage.removeItem(LOCKED_KEY);
  } catch {}
}

function defaultShift(date = new Date()): Shift {
  const h = date.getHours();
  if (h >= 6 && h < 14) return "morning";
  if (h >= 14 && h < 22) return "afternoon";
  return "night";
}

function readShiftStartedAt(): string {
  if (typeof window === "undefined") return new Date().toISOString();
  try {
    const cached = window.sessionStorage.getItem(SHIFT_STARTED_KEY);
    if (cached) return cached;
    const now = new Date().toISOString();
    window.sessionStorage.setItem(SHIFT_STARTED_KEY, now);
    return now;
  } catch {
    return new Date().toISOString();
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data, status } = useSession();
  const currentUserId = data?.user?.id ?? null;
  // El estado inicial es "locked" sólo si hay un userId guardado; cuando llegue
  // la sesión, el efecto de abajo lo invalida si no matchea el user actual.
  const [locked, setLockedState] = useState<boolean>(
    () => readLockedUserId() !== null,
  );
  const [shiftStartedAt, setShiftStartedAt] = useState<string | null>(null);

  // Inicializamos shiftStartedAt al primer mount (cliente).
  useEffect(() => {
    setShiftStartedAt(readShiftStartedAt());
  }, []);

  // Fijamos la TZ activa del cliente con la del tenant logueado, así todo el
  // formateo de fechas en la UI sale en la zona del tenant (no la del navegador).
  // Para un tenant en Argentina (default) no cambia nada. Nota: corre post-mount;
  // para un tenant no-AR el primer pintado usa el default hasta este efecto (sin
  // tenants no-AR hoy, no se nota; el fix fino sería sembrar la TZ desde el HTML).
  const tenantTimezone = data?.user?.tenantTimezone ?? null;
  useEffect(() => {
    setActiveTz(tenantTimezone ?? DEFAULT_TZ);
  }, [tenantTimezone]);

  // Sincroniza el lock con el usuario actual. Si la sesión está autenticada
  // pero el lock guardado pertenece a otro userId (o no hay), lo limpia. Si
  // no hay sesión, también lo limpia para que el próximo login arranque sin
  // pantalla de bloqueo.
  useEffect(() => {
    if (status === "loading") return;
    const stored = readLockedUserId();
    if (status !== "authenticated" || !currentUserId) {
      if (stored !== null) writeLockedUserId(null);
      setLockedState(false);
      return;
    }
    if (stored !== null && stored !== currentUserId) {
      writeLockedUserId(null);
      setLockedState(false);
    } else {
      setLockedState(stored === currentUserId);
    }
  }, [status, currentUserId]);

  const lock = useCallback(
    (reason: "idle" | "manual" = "idle") => {
      if (!currentUserId) return;
      writeLockedUserId(currentUserId);
      setLockedState(true);
      // Fire-and-forget: el audit no debe demorar ni romper el bloqueo de la UI.
      void recordScreenLock(reason);
    },
    [currentUserId],
  );

  const unlock = useCallback(() => {
    writeLockedUserId(null);
    setLockedState(false);
  }, []);

  const logout = useCallback(async () => {
    // Mostrar overlay antes de tocar nada. El flag de auth-transition persiste
    // en sessionStorage y el boot script del root layout lo repinta del otro
    // lado del hard reload — mismo patrón que el login (login/page.tsx).
    showAuthTransition();
    try {
      window.sessionStorage.removeItem(SHIFT_STARTED_KEY);
    } catch {}
    writeLockedUserId(null);
    setLockedState(false);
    resetUserContext();
    // Wrapper móvil: dar de baja el token de push ANTES de cerrar sesión (el
    // DELETE necesita la cookie viva). Acotado a 1.5s para no demorar el
    // logout si la red está mala; si no llega, el próximo login re-asigna el
    // token al usuario nuevo igual (upsert por token en /api/push/register).
    try {
      await Promise.race([
        teardownNativePush(),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
    } catch {}
    // Hard navigation (no `router.replace`): el navegador congela el frame
    // actual —con el overlay pintado— hasta que /login carga. Una navegación
    // RSC, en cambio, deja el AppShell montado durante el fetch y, como signOut
    // ya puso status="unauthenticated", asomaría el SessionExpiredScreen → el
    // flicker "Tu sesión expiró". El `finally` garantiza el navigate aunque
    // signOut falle (si no, el overlay quedaría colgado para siempre).
    try {
      await signOut({ redirect: false });
    } finally {
      window.location.assign("/login");
    }
  }, []);

  // Identificá al user en PostHog apenas tengamos la session completa. Esto
  // sobreescribe el identify "ligero" que hace el login (solo identifier) con
  // person properties completas (name, email, role, tenant, agency) y registra
  // super properties que se adjuntan a TODOS los eventos posteriores.
  const userId = data?.user?.id ?? null;
  const userName = data?.user?.name ?? null;
  const userEmail = data?.user?.email ?? null;
  const userRole = data?.user?.role ?? null;
  const userTenant = data?.user?.tenantSlug ?? null;
  const userAgency = data?.user?.agencySlug ?? null;
  useEffect(() => {
    if (status !== "authenticated" || !userId || !userRole || !userTenant || !userAgency) {
      return;
    }
    setUserContext({
      id: userId,
      name: userName ?? "",
      email: userEmail,
      role: userRole,
      tenant: userTenant,
      agency: userAgency,
      shift: defaultShift(),
    });
  }, [status, userId, userName, userEmail, userRole, userTenant, userAgency]);

  const session = useMemo<AuthSession | null>(() => {
    if (status !== "authenticated" || !data?.user?.id) return null;
    const u = data.user;
    // "Autenticado sin contexto de agencia" (multi-agencia / superadmin antes
    // del picker) se trata como sin sesión para los componentes de la app: solo
    // ocurre fuera del shell [agency], donde nada consume `session` salvo
    // `logout` (que es independiente de este memo).
    if (!u.agencyId || !u.agencySlug || !u.role) return null;
    return {
      user: {
        id: u.id,
        name: u.name ?? "",
        email: u.email ?? null,
        documentId: u.documentId ?? null,
        role: u.role,
        tenantId: u.tenantId,
        tenantSlug: u.tenantSlug,
        tenantTimezone: u.tenantTimezone ?? DEFAULT_TZ,
        agencyId: u.agencyId,
        agencySlug: u.agencySlug,
        tenantRole: u.tenantRole ?? null,
        multiContext: u.multiContext ?? false,
        sessionId: u.sessionId,
        pilotEnded: u.pilotEnded === true,
        mustResetPassword: u.mustResetPassword,
        active: true,
      },
      shift: defaultShift(),
      shiftStartedAt: shiftStartedAt ?? new Date().toISOString(),
      locked,
    };
  }, [data, status, shiftStartedAt, locked]);

  const value = useMemo<AuthContextValue>(
    () => ({ session, status, locked, lock, unlock, logout }),
    [session, status, locked, lock, unlock, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useCurrentUser(): AuthSessionUser | null {
  const { session } = useAuth();
  return session?.user ?? null;
}

export function useRequireRoles(roles: Role[]): boolean {
  const user = useCurrentUser();
  if (!user) return false;
  return roles.includes(user.role);
}
